import React, { useState, useContext, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from "./components/Navbar";
import MenuMobile from "./components/MenuMobile";
import Facturas from "./pages/Facturas";
import Egresos from "./pages/Egresos";
import Reportes from "./pages/Reportes";
import Clientes from "./pages/Clientes";
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Cuenta from './pages/Cuenta';

import { AuthContext } from './context/AuthContext';
import { ThemeContext } from './context/ThemeContext';

import Landing from './pages/Landing';
import Inicio from './pages/Inicio';
import Contador from './pages/Contador';
import Cobranzas from './pages/Cobranzas';
import AceptarContador from './pages/AceptarContador';
import ContadorGuestView from './pages/ContadorGuestView';
import { isViewerHome } from './utils/access';
import { canAccessVistaContador } from './utils/contadorAccess';
import { canAccessTeam } from './utils/teamAccess';
import Planes from './pages/Planes';
import QuienesSomos from './pages/QuienesSomos';
import Funcionalidades from './pages/Funcionalidades';
import AdminUsers from './pages/AdminUsers';
import AdminCompanies from './pages/AdminCompanies';
import AdminPlans from './pages/AdminPlans';
import AdminDashboard from './pages/AdminDashboard';
import AdminSystem from './pages/AdminSystem';
import AdminActivity from './pages/AdminActivity';
import AdminBetaProgram from './pages/AdminBetaProgram';
import AdminFiscal from './pages/AdminFiscal';
import Ayuda from './pages/Ayuda';
import Contacto from './pages/Contacto';
import Terminos from './pages/Terminos';
import Privacidad from './pages/Privacidad';
import ClientesDetalle from './pages/ClientesDetalle';
import PaisesDetalle from './pages/PaisesDetalle';
import FacturasImpagasDetalle from './pages/FacturasImpagasDetalle';
import DistribucionClientesDetalle from './pages/DistribucionClientesDetalle';
import EquipoUsuarios from './pages/EquipoUsuarios';
import Auditoria from './pages/Auditoria';
import CompletarPerfil from './pages/CompletarPerfil';
import UsoPlan from './pages/UsoPlan';
import PagoResultado from './pages/PagoResultado';

import PageTransition from './components/PageTransition';
import HardwareAccelerationBanner from './components/HardwareAccelerationBanner';
import QuickActionsFab from './components/QuickActionsFab';
import PwaInstallBanner from './components/PwaInstallBanner';
import LocalhostPwaWarning from './components/LocalhostPwaWarning';
import SeoHead from './components/SeoHead';
import {
  needsSubscriptionPayment,
  isPaymentCompletionPath,
  guestNeedsOnboarding,
} from './utils/subscriptionGate';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const location = useLocation();
  if (loading) {
    const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
    const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
        <div className="relative">
          <div className="animate-pulse">
            <img src={modoOscuro ? "/logowhite.png" : "/logoblack.png"} alt="Contsuite" className="h-12 w-auto" />
          </div>
          <div className="absolute inset-0 -m-8">
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-500/30' : 'border border-blue-500/30'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`} />
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-400/20' : 'border border-blue-400/20'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.5s]`} />
          </div>
        </div>
        <div className="mt-8 text-center space-y-2">
          <p className={`text-sm font-medium ${textPri}`}>Verificando sesión</p>
          <p className={`text-xs ${textSec}`}>Cargando tu información...</p>
        </div>
        <div className="mt-6 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '0ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '150ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (
    needsSubscriptionPayment(user)
    && !isPaymentCompletionPath(location.pathname)
  ) {
    return <Navigate to="/planes?pago=pendiente" replace />;
  }
  return children;
};

const GuestRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  if (loading) {
    const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
    const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
        <div className="relative">
          <div className="animate-pulse">
            <img src={modoOscuro ? "/logowhite.png" : "/logoblack.png"} alt="Contsuite" className="h-12 w-auto" />
          </div>
        </div>
        <div className="mt-8 text-center space-y-2">
          <p className={`text-sm font-medium ${textPri}`}>Verificando sesión</p>
          <p className={`text-xs ${textSec}`}>Cargando tu información...</p>
        </div>
      </div>
    );
  }
  if (user) {
    if (user.role === 'admin' && user.isSuperAdmin === true) {
      return <Navigate to="/inicio" replace />;
    }
    if (guestNeedsOnboarding(user)) {
      return <Navigate to="/completar-perfil" replace />;
    }
    if (needsSubscriptionPayment(user)) {
      return <Navigate to="/planes?pago=pendiente" replace />;
    }
    return <Navigate to={isViewerHome(user) ? '/contador' : '/inicio'} replace />;
  }
  return children;
};

const ContadorRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  if (!canAccessVistaContador(user)) return <Navigate to="/inicio" replace />;
  return children;
};

const TeamRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  if (loading) {
    const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
    const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
        <div className="relative">
          <div className="animate-pulse">
            <img src={modoOscuro ? "/logowhite.png" : "/logoblack.png"} alt="Contsuite" className="h-12 w-auto" />
          </div>
          <div className="absolute inset-0 -m-8">
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-500/30' : 'border border-blue-500/30'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`} />
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-400/20' : 'border border-blue-400/20'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.5s]`} />
          </div>
        </div>
        <div className="mt-8 text-center space-y-2">
          <p className={`text-sm font-medium ${textPri}`}>Verificando acceso</p>
          <p className={`text-xs ${textSec}`}>Comprobando permisos del equipo...</p>
        </div>
        <div className="mt-6 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '0ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '150ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  return canAccessTeam(user) ? children : <Navigate to="/planes" replace />;
};

const inferPlanSlugFromFeatures = (unlockedFeatures) => {
  if (!unlockedFeatures) return null;

  // Enterprise (todo avanzado)
  if (
    unlockedFeatures.advancedAnalytics &&
    unlockedFeatures.prioritySupport &&
    unlockedFeatures.employeeAccounts &&
    unlockedFeatures.multiCompany &&
    unlockedFeatures.customTemplates &&
    unlockedFeatures.logoUpload &&
    unlockedFeatures.companyBranding
  ) {
    return 'enterprise';
  }

  // Empresarial
  if (unlockedFeatures.employeeAccounts && unlockedFeatures.multiCompany) {
    return 'empresarial';
  }

  // Profesional
  if (unlockedFeatures.customTemplates && unlockedFeatures.logoUpload) {
    return 'profesional';
  }

  // Básico
  if (unlockedFeatures.basicInvoicing && unlockedFeatures.basicTemplates) {
    return 'basico';
  }

  return null;
};

// Ruta protegida adicional para secciones de administración
// Regla de negocio: solo Super Admin o planes Empresarial / Enterprise
const AdminRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);

  const { modoOscuro } = useContext(ThemeContext);
  if (loading) {
    const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
    const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
        <div className="relative">
          <div className="animate-pulse">
            <img src={modoOscuro ? "/logowhite.png" : "/logoblack.png"} alt="Contsuite" className="h-12 w-auto" />
          </div>
          <div className="absolute inset-0 -m-8">
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-500/30' : 'border border-blue-500/30'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`} />
            <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-400/20' : 'border border-blue-400/20'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.5s]`} />
          </div>
        </div>
        <div className="mt-8 text-center space-y-2">
          <p className={`text-sm font-medium ${textPri}`}>Verificando permisos</p>
          <p className={`text-xs ${textSec}`}>Cargando información administrativa...</p>
        </div>
        <div className="mt-6 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '0ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '150ms' }} />
          <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin siempre tiene acceso
  if (user.role === 'admin' && user.isSuperAdmin === true) {
    return children;
  }

  // Para el resto, depender solo del plan actual
  const directSlug = user.currentPlan && typeof user.currentPlan === 'object'
    ? user.currentPlan.slug
    : null;
  const inferredSlug = inferPlanSlugFromFeatures(user.unlockedFeatures);
  const planSlug = ((directSlug || inferredSlug) || '').toLowerCase();

  // Evitar decisiones prematuras: si todavía no tenemos slug (y no pudimos inferirlo),
  // esperamos a que el user se hidrate desde /api/users/me
  if (!planSlug) {
    return <div>Cargando permisos...</div>;
  }

  const hasAccessByPlan = ['empresarial', 'enterprise'].includes(planSlug);

  if (!hasAccessByPlan) {
    return <Navigate to="/planes" replace />;
  }

  return children;
};



function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [visible, setVisible] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setVisible(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setVisible(true);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Exponer función global para mostrar el banner manualmente
    window.showOfflineBanner = () => setVisible(true);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      delete window.showOfflineBanner;
    };
  }, []);

  if (!isOffline || !visible) return null;
  return (
    <div className="fixed top-0 left-0 w-full z-50 bg-red-600 text-white text-center py-2 font-semibold shadow-md animate-pulse flex items-center justify-center">
      <span>Sin conexión a internet. Algunos datos pueden no estar actualizados.</span>
      <button
        onClick={() => setVisible(false)}
        className="ml-4 px-2 py-0.5 rounded bg-red-800 hover:bg-red-900 text-white font-bold text-lg"
        aria-label="Cerrar aviso de sin conexión"
      >
        ×
      </button>
    </div>
  );
}

function App() {
  const [menuMobileOpen, setMenuMobileOpen] = useState(false);
  const { modoOscuro } = useContext(ThemeContext);
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);

  // Monitorear estado de conexión
  useEffect(() => {
    const handleOnline = () => {
      setShowOfflineBanner(false);
    };
    
    const handleOffline = () => {
      setShowOfflineBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const appShellClass = modoOscuro
    ? 'min-h-screen bg-black text-white'
    : 'min-h-screen bg-[#f5f5f7] text-gray-900';

  return (
    <Router>
        <LocalhostPwaWarning />
        {/* Banner de estado offline */}
        {showOfflineBanner && (
          <div className={`fixed top-0 left-0 right-0 z-50 p-4 text-center font-semibold ${
            modoOscuro 
              ? 'bg-yellow-900 text-yellow-200 border-b border-yellow-700' 
              : 'bg-yellow-100 text-yellow-900 border-b border-yellow-300'
          }`}>
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>Sin conexión a internet. Los cambios se guardarán localmente.</span>
              <button
                onClick={() => setShowOfflineBanner(false)}
                className="ml-2 text-sm underline hover:no-underline"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        <OfflineBanner />
        <SeoHead />
        <Routes>
          <Route path="/login" element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          } />
          <Route path="/register" element={
            <GuestRoute>
              <Register />
            </GuestRoute>
          } />
          <Route path="/forgot-password" element={
            <GuestRoute>
              <ForgotPassword />
            </GuestRoute>
          } />
          <Route path="/restablecer-clave" element={
            <ResetPassword />
          } />
          <Route path="/aceptar-contador" element={<AceptarContador />} />
          <Route path="/contador/compartido/:token" element={<ContadorGuestView />} />
          <Route path="/completar-perfil" element={<CompletarPerfil />} />
          {/* Rutas de Administración */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminDashboard />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/users" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminUsers />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/companies" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminCompanies />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/plans" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminPlans />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/system" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminSystem />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/activity" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminActivity />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/beta" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminBetaProgram />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/fiscal" element={
            <PrivateRoute>
              <AdminRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full flex-1 flex flex-col min-h-0">
                    <AdminFiscal />
                  </main>
                </div>
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/negocio" element={<Navigate to="/facturas" replace />} />

          {/* Equipo (Empresarial) */}
          <Route path="/equipo/usuarios" element={
            <PrivateRoute>
              <TeamRoute>
                <div className={appShellClass}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="w-full min-h-[60vh]">
                    <EquipoUsuarios />
                  </main>
                </div>
              </TeamRoute>
            </PrivateRoute>
          } />
          <Route path="/auditoria" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Auditoria />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/equipo/auditoria" element={<Navigate to="/auditoria" replace />} />
          <Route path="/clientes" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Clientes />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/clientes-detalle" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <ClientesDetalle />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/uso-plan" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <UsoPlan />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/pago/resultado" element={<PagoResultado />} />
          <Route path="/activar-cuenta" element={<PagoResultado />} />
          <Route path="/facturas" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Facturas />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/egresos" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Egresos />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/cobranzas" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Cobranzas />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/reportes" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Reportes />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/home" element={
            <PrivateRoute>
              <div className={`${appShellClass} flex flex-col min-h-screen`}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="flex-1 w-full overflow-x-hidden">
                  <Inicio />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/inicio" element={
            <PrivateRoute>
              <div className={`${appShellClass} flex flex-col min-h-screen`}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="flex-1 w-full overflow-x-hidden">
                  <Inicio />
                </main>
              </div>
            </PrivateRoute>
          } />
          {/* Vista móvil deshabilitada temporalmente */}
          <Route path="/movil" element={<Navigate to="/inicio" replace />} />
          <Route path="/contador" element={
            <PrivateRoute>
              <ContadorRoute>
                <div className={`${appShellClass} flex flex-col min-h-screen`}>
                  <Navbar />
                  <MenuMobile
                    open={menuMobileOpen}
                    onClose={() => setMenuMobileOpen(false)}
                  />
                  <main className="flex-1 w-full overflow-x-hidden">
                    <Contador />
                  </main>
                </div>
              </ContadorRoute>
            </PrivateRoute>
          } />
          <Route path="/dashboard" element={<Navigate to="/inicio" replace />} />
          <Route path="/paises-detalle" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <PaisesDetalle />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/facturas-impagas-detalle" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <FacturasImpagasDetalle />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/distribucion-clientes-detalle" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <DistribucionClientesDetalle />
                </main>
              </div>
            </PrivateRoute>
          } />
          <Route path="/cuenta" element={
            <PrivateRoute>
              <div className={appShellClass}>
                <Navbar
                />
                <MenuMobile
                  open={menuMobileOpen}
                  onClose={() => setMenuMobileOpen(false)}
                />
                <main className="w-full min-h-[60vh]">
                  <Cuenta />
                </main>
              </div>
            </PrivateRoute>
          } />
          {/* Ruta de plantillas eliminada */}
          <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
          <Route path="/planes" element={<PageTransition><Planes /></PageTransition>} />
          <Route path="/quienes-somos" element={<PageTransition><QuienesSomos /></PageTransition>} />
          <Route path="/ayuda" element={<PageTransition><Ayuda /></PageTransition>} />
          <Route path="/funcionalidades" element={<PageTransition><Funcionalidades /></PageTransition>} />
          <Route path="/contacto" element={<PageTransition><Contacto /></PageTransition>} />
          <Route path="/terminos" element={<PageTransition><Terminos /></PageTransition>} />
          <Route path="/privacidad" element={<PageTransition><Privacidad /></PageTransition>} />
          <Route path="*" element={<div className="p-8">Página no encontrada</div>} />
        </Routes>

        {/* Banner para habilitar aceleración por hardware en toda la app */}
        <HardwareAccelerationBanner />
        <PwaInstallBanner />
        <QuickActionsFab />
      </Router>
  );
}

export default App;
